/**
 * preload.ts
 * ----------------
 * Reducers for populating application state with preloaded values.
 */

import { Record } from 'immutable';

import { Action, PayloadAction } from '../../actions/types';
import { PreloadError } from '../../constants/actions';
import { ErrorResponse } from '../../models/common';

interface PreloadStateFields {
    // Important note: preloadError defaults to undefined (to indicate no error), but we shouldn't use ? here: a value
    // should explicitly be given. Reason: if value is not given when initializing state, value cannot be set later.
    preloadError: ErrorResponse | undefined;
}

/**
 * Application state used by applications for prepopulated state.
 */
export class PreloadState extends Record<PreloadStateFields>({
    // Important note: explicitly setting this to undefined here. Reason: if this value is not given here, then .set()
    // operation on this property will be a no-op. Strange behavior from Immutable.
    preloadError: undefined,
}) {}

const initialState = new PreloadState();

const preloadErrorReducer = (state: PreloadState, action: PayloadAction<ErrorResponse>) =>
    state.set('preloadError', action.payload);

/**
 * Reducer that updates preloaded parts of the application state.
 * @param state Current application state.
 * @param action Action to be performed on state.
 * @returns Updated application state.
 */
export function preloadReducer (state: PreloadState = initialState, action?: Action) {
    // Pass back input state if no action given
    if (action === undefined) {
        return state;
    }

    switch (action.type) {
        case PreloadError:
            return preloadErrorReducer(state, action as PayloadAction<ErrorResponse>);

        default:
            return state;
    }
}
